import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, Shield, Zap, Users, BarChart3, Sparkles, Play } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PlanLanding = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [appName, setAppName] = useState('Thrive');
  const [appLogo, setAppLogo] = useState('');
  const [demoVideo, setDemoVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const referralCode = searchParams.get('ref') || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [planRes, videoRes] = await Promise.all([
          fetch(`${API}/api/public/plan/${planId}`),
          fetch(`${API}/api/demo-videos/active`)
        ]);
        if (planRes.ok) {
          const data = await planRes.json();
          setPlan(data.plan);
          setAppName(data.app_name || 'Thrive');
          setAppLogo(data.app_logo || '');
        }
        if (videoRes.ok) {
          const vData = await videoRes.json();
          if (vData.video) setDemoVideo(vData.video);
        }
      } catch (err) { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [planId]);

  // Also fetch app settings for logo
  useEffect(() => {
    fetch(`${API}/api/app-settings/public`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.app_logo) setAppLogo(d.app_logo);
      if (d?.app_name) setAppName(d.app_name);
    }).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen honeycomb-bg flex items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen honeycomb-bg flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-stone-800 mb-2">Plan not found</h1>
        <p className="text-stone-500 mb-6">This plan may no longer be available.</p>
        <Button onClick={() => navigate('/pricing')}>View All Plans</Button>
      </div>
    );
  }

  const isFree = plan.price_monthly === 0;
  const signupUrl = `/trial-signup?plan=${planId}${referralCode ? `&ref=${referralCode}` : ''}`;

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const embedUrl = demoVideo ? getYouTubeEmbedUrl(demoVideo.video_url) : null;

  return (
    <div className="min-h-screen honeycomb-bg">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-amber-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/trial-signup')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            {appLogo ? (
              <img src={`${API}/api/uploads/${appLogo}`} alt={appName} className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" />
            ) : (
              <div className="h-9 w-9 rounded-lg flex items-center justify-center">
                <img src="/thrive-logo.png" alt={appName} className="h-9 w-9 object-contain" />
              </div>
            )}
            <span className="font-bold text-stone-800 text-lg">{appName}</span>
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/pricing')} className="text-sm">All Plans</Button>
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm">Login</Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-14 pb-10 text-center">
        {referralCode && (
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Users className="h-4 w-4" />
            You were invited by a fellow beekeeper!
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 mb-3">
          {plan.name}
          {isFree && <span className="text-amber-500"> Plan</span>}
        </h1>
        
        <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-8">
          {plan.description || `Everything you need to manage your beekeeping operation with ${appName}.`}
        </p>

        {/* Pricing */}
        <div className="inline-block bg-white rounded-2xl shadow-lg border border-stone-200 p-8 mb-8">
          {isFree ? (
            <div>
              <div className="text-5xl font-black text-amber-500 mb-1">FREE</div>
              <p className="text-stone-500">No credit card required</p>
            </div>
          ) : (
            <div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-black text-stone-900">${plan.price_monthly}</span>
                <span className="text-stone-500 text-lg">/month</span>
              </div>
              {plan.price_annual > 0 && (
                <p className="text-stone-500 mt-1">or ${plan.price_annual}/year (save {Math.round((1 - plan.price_annual / (plan.price_monthly * 12)) * 100)}%)</p>
              )}
            </div>
          )}
        </div>

        <div>
          <Button 
            onClick={() => navigate(signupUrl)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-10 py-6 text-lg font-bold rounded-full shadow-lg shadow-amber-200"
            data-testid="plan-signup-btn"
          >
            {isFree ? 'Get Started Free' : `Start with ${plan.name}`}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Plan Limits */}
      <div className="max-w-4xl mx-auto px-4 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 text-center border border-stone-200 shadow-sm">
            <div className="text-3xl font-black text-blue-600">{plan.max_hives || '2'}</div>
            <div className="text-sm text-stone-500 mt-1">Hives</div>
          </div>
          <div className="bg-white rounded-xl p-5 text-center border border-stone-200 shadow-sm">
            <div className="text-3xl font-black text-teal-600">{plan.max_apiaries ? plan.max_apiaries : 'Unlimited'}</div>
            <div className="text-sm text-stone-500 mt-1">Apiaries</div>
          </div>
          <div className="bg-white rounded-xl p-5 text-center border border-stone-200 shadow-sm">
            <div className="text-3xl font-black text-purple-600">{plan.max_crew || 1}</div>
            <div className="text-sm text-stone-500 mt-1">Crew Members</div>
          </div>
          <div className="bg-white rounded-xl p-5 text-center border border-stone-200 shadow-sm">
            <div className="text-3xl font-black text-amber-600">{plan.max_inspections || 200}</div>
            <div className="text-sm text-stone-500 mt-1">Inspections/mo</div>
          </div>
        </div>
      </div>

      {/* Features List */}
      {plan.features && plan.features.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 pb-14">
          <h2 className="text-2xl font-bold text-stone-800 text-center mb-6">What's Included</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {plan.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-stone-100 shadow-sm">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <span className="text-stone-700 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Section */}
      {demoVideo && (
        <div className="max-w-3xl mx-auto px-4 pb-14">
          <h2 className="text-2xl font-bold text-stone-800 text-center mb-6">See {appName} in Action</h2>
          {embedUrl ? (
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-stone-200" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={embedUrl}
                title={demoVideo.title}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <a href={demoVideo.video_url} target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center gap-3 bg-white rounded-2xl p-8 border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
              <Play className="h-8 w-8 text-amber-500" />
              <div>
                <p className="font-bold text-stone-800">{demoVideo.title}</p>
                <p className="text-sm text-stone-500">{demoVideo.description}</p>
              </div>
            </a>
          )}
        </div>
      )}

      {/* Feature Highlights */}
      <div className="bg-stone-900 text-white py-14">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-10">Why Beekeepers Love {appName}</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <Shield className="h-8 w-8 mx-auto mb-3 text-amber-400" />
              <h3 className="font-bold mb-1">Secure & Private</h3>
              <p className="text-sm text-stone-400">Your data is encrypted and never shared with third parties</p>
            </div>
            <div>
              <Zap className="h-8 w-8 mx-auto mb-3 text-amber-400" />
              <h3 className="font-bold mb-1">Works Offline</h3>
              <p className="text-sm text-stone-400">Record inspections in the field with no signal, sync when connected</p>
            </div>
            <div>
              <BarChart3 className="h-8 w-8 mx-auto mb-3 text-amber-400" />
              <h3 className="font-bold mb-1">Smart Analytics</h3>
              <p className="text-sm text-stone-400">Track hive health trends, colony performance, and harvest data</p>
            </div>
            <div>
              <Sparkles className="h-8 w-8 mx-auto mb-3 text-amber-400" />
              <h3 className="font-bold mb-1">AI Assistant</h3>
              <p className="text-sm text-stone-400">Get smart suggestions and insights powered by AI for your apiary</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h2 className="text-2xl font-bold text-stone-800 mb-4">Ready to get started?</h2>
        <Button 
          onClick={() => navigate(signupUrl)}
          className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-5 text-lg font-bold rounded-full"
        >
          {isFree ? 'Sign Up Free' : `Choose ${plan.name}`}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <p className="text-sm text-stone-400 mt-4">No credit card required. Cancel anytime.</p>
      </div>
    </div>
  );
};

export default PlanLanding;
